import {Component, Input} from '@angular/core';
import {CommonModule} from '@angular/common';
import {WeeklyDirection} from '@shared/api/models/query/patient-weekly-snapshot.model';

/**
 * "Your observations" per-area trend bar (UNIVAPLAT-2360 follow-up — design correction).
 *
 * Distinct from `cdss-weekly-trend-sparkline` (a continuous numeric line chart, still
 * used for the ED-15 overall score): this renders the observation areas' 8-slot history
 * as 8 small boxes, each one of exactly one of these (per-slot step-by-step spec,
 * UNIVAPLAT-2360 follow-up) — never a grey box showing through underneath a band/fill:
 *   1. A plain light-grey box — the whole rendering for a missing/not-yet-answered
 *      day (`null`, "no values").
 *   2. 'noChange' (a genuine answer — clinically distinct from a missing one): a
 *      darker-grey band, vertically centred, ~4px tall, spanning the box's full
 *      width, with no grey box around it.
 *   3. 'better': a green fill, from the box's top edge down to where the 'noChange'
 *      band's bottom edge would sit (i.e. half the box plus half the band).
 *   4. 'worse': a yellow fill, from where the band's top edge would sit down to the
 *      box's bottom edge — the mirror image of 'better'.
 * Green/yellow match the score-background colours used elsewhere on this page (e.g.
 * weekly-survey-card's ED-15 subclinical/moderate severity chips) — not
 * `cdss-weekly-direction-badge`'s own darker green/yellow, which were chosen as text
 * colours on a pale chip background and read as too dark/saturated for a solid box
 * fill. The host has a fixed, compact width (not stretched to fill its row) — see
 * the component's own `:host` rule.
 */
@Component({
    selector: 'cdss-weekly-observation-trend-bar',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './weekly-observation-trend-bar.component.html',
    styleUrl: './weekly-observation-trend-bar.component.scss'
})
export class WeeklyObservationTrendBarComponent {

    @Input() values: (WeeklyDirection | null)[] = [];

    /** Optional per-slot labels (e.g. weekday or Ymd) used only to build the generated aria-label. */
    @Input() pointLabels?: (string | null)[];

    /** Overrides the generated aria-label entirely. */
    @Input() ariaLabel?: string;

    trackByIndex(index: number): number {
        return index;
    }

    private describe(value: WeeklyDirection | null): string {
        switch (value) {
            case 'better':
                return 'better';
            case 'worse':
                return 'worse';
            case 'noChange':
                return 'no change';
            default:
                return 'not answered';
        }
    }

    get computedAriaLabel(): string {
        if (this.ariaLabel) return this.ariaLabel;
        const parts = this.values.map((value, index) => {
            const label = this.pointLabels?.[index] ?? `day ${index + 1}`;
            return `${label}: ${this.describe(value)}`;
        });
        return `Trend: ${parts.join(', ')}`;
    }
}
